// components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// queries
import { queries } from 'RestAPIs/rapini';

// utils
import {
  DELETED_APPOINTMENT_CLASS,
  getSyncStatusLogRowId,
  SyncStatusLogRow,
  useColumnDefinitions
} from './SynchronisationLogs.utils';
import {
  computeRange,
  toDate
} from 'components/shared/DateNavigation/DateNavigation.utils';

// styles
import {
  DateNavContainer,
  LogsPageContainer,
  SynchronisationLogsDateNavigationContainer,
  SynchronisationLogsFiltersContainer,
  SynchronisationLogsRefreshContainer,
  SynchronisationLogsRefreshButton
} from 'components/logs/logs.styles';

// types
import { DateNavigation } from 'components/shared/index';

// libraries
import { Tooltip } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@sdb/planning-ui';

// stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';
import { useShallow } from 'zustand/shallow';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { SingleOrRange } from 'components/shared/DateNavigation';
import AppointmentSummary from './components/AppointmentSummary/AppointmentSummary';

const SynchronisationLogs = () => {
  const { t } = useTranslation('common');
  const { selectedDate, setSelectedDate } = useDateNavigationHoursStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
      setSelectedDate: state.setSelectedDate
    }))
  );

  const period = useDateNavigationStore(useShallow((state) => state.period));
  const appliedPeriod = period ?? 'day';

  const { startDate, endDate } = useMemo(() => {
    return computeRange(selectedDate, appliedPeriod);
  }, [appliedPeriod, selectedDate]);

  const {
    data: synchronisationLogs,
    isLoading,
    isError,
    refetch
  } = queries.useGetCalculatedAppointmentOccurrencesSyncStatuses(
    startDate,
    endDate,
    'FAILED'
  );

  const handleSelectedDateChange = useCallback<(date: SingleOrRange) => void>(
    (date) => {
      setSelectedDate(Array.isArray(date) || !date ? null : toDate(date));
    },
    [setSelectedDate]
  );

  const columns = useColumnDefinitions();
  const rows = (synchronisationLogs || []) as SyncStatusLogRow[];
  const refreshLabel = t('logsPortal.synchronisationLogs.refresh');

  return (
    <LogsPageContainer>
      <DateNavContainer>
        <SynchronisationLogsFiltersContainer>
          <div />

          <SynchronisationLogsDateNavigationContainer>
            <DateNavigation
              allowMenuOptions
              defaultDate={selectedDate}
              defaultPeriod="day"
              onDateChange={handleSelectedDateChange}
              options={['day', 'week']}
            />
          </SynchronisationLogsDateNavigationContainer>

          <SynchronisationLogsRefreshContainer>
            <Tooltip title={refreshLabel}>
              <SynchronisationLogsRefreshButton
                aria-label={refreshLabel}
                onClick={() => {
                  void refetch();
                }}
              >
                <Icon type="arrow-rotate-right" fontSize="small" />
              </SynchronisationLogsRefreshButton>
            </Tooltip>
          </SynchronisationLogsRefreshContainer>
        </SynchronisationLogsFiltersContainer>
      </DateNavContainer>

      <TableCRUDLayout
        loading={isLoading}
        columns={columns}
        sx={{
          [`.${DELETED_APPOINTMENT_CLASS}`]: {
            textDecoration: 'line-through',
            opacity: 0.55
          }
        }}
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'appointmentDate',
                sort: 'asc'
              },
              {
                field: 'destination',
                sort: 'asc'
              }
            ]
          }
        }}
        onClose={refetch}
        rows={rows}
        slots={{
          row: (gridRow) => (
            <AppointmentSummary
              appointmentSummary={
                gridRow.row.appointmentContext?.appointmentSummary
              }
              gridRow={gridRow}
            />
          )
        }}
        // Arbitrary concatenated string to ensure each row is unique
        getRowId={(log) => getSyncStatusLogRowId(log as SyncStatusLogRow)}
        hasLoadingError={isError}
        onLoadingError={refetch}
        disableActions
      />
    </LogsPageContainer>
  );
};

export default SynchronisationLogs;
